import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { Account } from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateAccountName } from '@/hooks/admin/use-update-account-name';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';

interface EditAccountNameDialogProps {
  open: boolean;
  account: Account | null;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditAccountNameDialog({
  open,
  account,
  onOpenChange,
  onUpdated,
}: EditAccountNameDialogProps) {
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { updateAccountName, isLoading } = useUpdateAccountName();

  useEffect(() => {
    if (!open || !account) {
      setErrorMessage(null);
      return;
    }

    setName(account.name);
    setErrorMessage(null);
  }, [open, account]);

  const trimmedName = name.trim();
  const currentName = account?.name.trim() ?? '';
  const isSubmitDisabled =
    !account ||
    trimmedName.length === 0 ||
    trimmedName === currentName ||
    isLoading;

  const handleSubmit = async () => {
    if (!account || isSubmitDisabled) {
      return;
    }

    setErrorMessage(null);

    try {
      await updateAccountName({
        accountId: account.id,
        name: trimmedName,
      });
      onUpdated();
      onOpenChange(false);
    } catch (error) {
      setErrorMessage(
        getRequestErrorMessage(error, 'Failed to update account name.')
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit name</DialogTitle>
          <DialogDescription>
            Update this account&apos;s name.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="account-name">Name</Label>
          <Input
            id="account-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrorMessage(null);
            }}
            placeholder="Account name"
          />
        </div>

        {errorMessage ? (
          <div className="text-sm text-rose-600">{errorMessage}</div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="relative"
          >
            <span className={isLoading ? 'invisible' : ''}>Confirm</span>
            {isLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
